import { Wizard, WizardStepRenderParams } from '@factorialco/deprecated-design-system'
import { CompetenciesDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  CompetenciesCompetenciesListDocument,
  CompetenciesCreateCompetencyDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useForm } from 'modules/core/hooks/useForm'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { defaultRange, generateOptionsByRange } from 'modules/competencies/lib/ratingSliderUtil'

import { AudienceStep } from './AudienceStep'
import { BasicInformationStep } from './BasicInformationStep'
import { WizardData } from './WizardData'

interface Props {
  isCompetenciesRevampEnabled: boolean
  onClose: () => void
}

export const NewCompetencyWizard: React.FC<Props> = ({ isCompetenciesRevampEnabled, onClose }) => {
  const [create, { loading: creating }] = useMutation(CompetenciesCreateCompetencyDocument, {
    extractorFn: (res) => res.competenciesMutations.createCompetenciesCompetency,
  })

  const form = useForm<WizardData>({
    defaultValues: {
      name: '',
      description: '',
      competencyGroupId: undefined,
      audience: isCompetenciesRevampEnabled ? 'jobs' : 'all',
      roleIds: [],
      options: generateOptionsByRange(defaultRange()),
    },
    async onSubmit(values, { setErrors }) {
      if (creating) return Promise.reject()

      const everyone = values.audience === 'all'
      const result = await create({
        variables: {
          name: values.name,
          description: values.description,
          competencyGroupId: values.competencyGroupId,
          everyone: everyone,
          roleIds: values.roleIds,
          options: values.options || [],
        },
        refetchQueries: [CompetenciesDataTableDocument, CompetenciesCompetenciesListDocument],
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('competencies_v3.new_competency_wizard.created_successfully'),
        })

        if (everyone) {
          track(trackingEvents.competencies.createCompetencyForEveryone)
        } else {
          track(trackingEvents.competencies.createCompetencyForAudience)
        }

        onClose()
      }

      return handled
    },
  })

  const formHandleSubmit = form.handleSubmit
  const onSubmit = useUnsafeCallback(() => formHandleSubmit(), [formHandleSubmit])

  const steps = useMemo(() => {
    return compact([
      {
        title: i18n.t('competencies_v3.new_competency_wizard.basic_information_step.title'),
        nextText: isCompetenciesRevampEnabled
          ? i18n.t('competencies_v3.new_competency_wizard.create_only')
          : i18n.t('competencies_v3.new_competency_wizard.next'),
        render: (props: WizardStepRenderParams<WizardData>) => <BasicInformationStep {...props} />,
        isCompleted: (data: WizardData) => !!data.name && !creating,
      },
      !isCompetenciesRevampEnabled && {
        title: i18n.t('competencies_v3.new_competency_wizard.audience_step.title'),
        nextText: i18n.t('competencies_v3.new_competency_wizard.create'),
        render: (props: WizardStepRenderParams<WizardData>) => <AudienceStep {...props} />,
        isCompleted: (data: WizardData) => !!data.audience && !creating,
      },
    ])
  }, [creating, isCompetenciesRevampEnabled]) // eslint-disable-next-line react-hooks/exhaustive-deps

  return (
    <Wizard.Modal
      initialData={form.initialValues}
      size="large"
      icon="window-user"
      title={i18n.t('competencies_v3.new_competency_wizard.title')}
      onClose={onClose}
      onSubmit={onSubmit}
      onDataChanged={form.setValues}
      steps={steps}
    />
  )
}
